import type { LogsInitConfiguration } from '@openobserve/browser-logs';
import type { RumInitConfiguration, User } from '@openobserve/browser-rum';

export type AnalyticsContext = Record<string, unknown>;

export interface AnalyticsUser {
  id: string;
  email?: string;
  username?: string;
  isStreamer?: boolean;
  isAdmin?: boolean;
}

export interface AnalyticsInitOptions {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  version: string;
  sessionReplaySampleRate: number;
}

// The slice of @openobserve/browser-rum this wrapper depends on. The vendor
// imports above are type-only and therefore erased, so the spec can exercise
// this file without loading a browser SDK into a node test.
export interface RumSdk {
  init(options: RumInitConfiguration): void;
  setUser(user: User): void;
  clearUser(): void;
  stopSession(): void;
  addAction(name: string, context?: AnalyticsContext): void;
  addError(error: unknown, context?: AnalyticsContext): void;
  startSessionReplayRecording(): void;
}

export interface LogsSdk {
  init(options: LogsInitConfiguration): void;
}

// Property syntax, not methods: analytics.ts re-exports each of these on its
// own, and a method type would make every one of those an unbound-method.
export interface Analytics {
  init: (options: AnalyticsInitOptions) => void;
  trackAction: (name: string, context?: AnalyticsContext) => void;
  identifyUser: (user: AnalyticsUser) => void;
  clearUser: () => void;
  reportError: (error: unknown, context?: AnalyticsContext) => void;
}

const toSdkUser = (user: AnalyticsUser): User => ({
  id: user.id,
  ...(user.email === undefined ? {} : { email: user.email }),
  ...(user.username === undefined ? {} : { name: user.username }),
  ...(user.isStreamer === undefined ? {} : { is_streamer: user.isStreamer }),
  ...(user.isAdmin === undefined ? {} : { is_admin: user.isAdmin }),
});

const sharedOptions = (options: AnalyticsInitOptions) => ({
  clientToken: options.clientToken,
  site: options.site,
  service: options.service,
  env: options.env,
  version: options.version,
  organizationIdentifier: 'default',
  apiVersion: 'v1',
  insecureHTTP: false,
});

// init runs after hydrateRoot on purpose, so the errors most worth knowing about
// -- a hydration failure above all -- happen before the SDK is up. They wait here
// instead of being dropped. Bounded, because a page with no client token never
// flushes.
const PENDING_ERROR_LIMIT = 20;

export const createAnalytics = ({ rum, logs }: { rum: RumSdk; logs: LogsSdk }): Analytics => {
  let started = false;
  let pendingErrors: readonly { error: unknown; context?: AnalyticsContext }[] = [];

  // Arrow properties rather than methods: the module singleton in analytics.ts
  // re-exports these individually, and an unbound method would be a scoping
  // hazard even though none of them touch `this`.
  const init = (options: AnalyticsInitOptions) => {
    // An empty token is the "ship it dark" state: the call sites land in a
    // deploy, and setting the Railway variable is what turns collection on.
    if (started || options.clientToken === '') {
      return;
    }
    started = true;

    rum.init({
      ...sharedOptions(options),
      applicationId: options.applicationId,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      // Never 'allow', which is what the vendor sample uses. This app renders
      // email addresses, OAuth client secrets and OTP codes.
      defaultPrivacyLevel: 'mask-user-input',
      // Preserves the posture posthog-js held with cookieless_mode: 'always'.
      sessionPersistence: 'memory',
      trackAnonymousUser: false,
    });

    logs.init({ ...sharedOptions(options), forwardErrorsToLogs: true });

    if (options.sessionReplaySampleRate > 0) {
      rum.startSessionReplayRecording();
    }

    const buffered = pendingErrors;
    pendingErrors = [];
    buffered.forEach(({ error, context }) => rum.addError(error, context));
  };

  const trackAction = (name: string, context?: AnalyticsContext) => {
    if (!started) {
      return;
    }
    rum.addAction(name, context);
  };

  const identifyUser = (user: AnalyticsUser) => {
    if (!started) {
      return;
    }
    rum.setUser(toSdkUser(user));
  };

  const clearUser = () => {
    if (!started) {
      return;
    }
    // posthog.reset() dropped the identity and rotated the anonymous id;
    // clearUser alone only does the first, leaving the next user on a shared
    // browser inside the previous session.
    rum.clearUser();
    rum.stopSession();
  };

  const reportError = (error: unknown, context?: AnalyticsContext) => {
    if (!started) {
      if (pendingErrors.length < PENDING_ERROR_LIMIT) {
        pendingErrors = [...pendingErrors, { error, context }];
      }
      return;
    }
    rum.addError(error, context);
  };

  return { init, trackAction, identifyUser, clearUser, reportError };
};
